import Editor, { useMonaco } from '@monaco-editor/react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, PlayIcon, RocketIcon } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import {
  createDeploy,
  runSpotTask,
  runTask,
  startProject,
} from '@/service/serverless-gpu';
import { Link, useParams } from 'react-router-dom';
import { RouteParams } from '@/router/type';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { toastSomethingError } from '@/components/ui/toast-content';
import { AxiosError } from 'axios';
import {
  CreateDeployEndpointParams,
  RunSpotTaskPayload,
  RunTaskPayload,
} from '@/service/serverless-gpu/type';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toBase64 } from '@/utils';
import { defaultCode } from './default';
import PATH_PAGE from '@/router/path';

const ServerlessGPUPlaygroundDeploy = () => {
  const { projectId, workspaceId } = useParams<RouteParams>();
  const [code, setCode] = useState<string>(defaultCode);
  const [fileName, setFileName] = useState<string>('deploy');
  const editorRef = useRef(null);

  function handleEditorDidMount(editor: any, monaco: any) {
    editorRef.current = editor;
  }

  const { mutate, isPending, data } = useMutation({
    mutationFn: async (params: CreateDeployEndpointParams) => {
      await startProject({
        project_id: projectId!,
        config: {
          mode: 'realtime',
        },
        requirements_content: '',
      });
      const data = await createDeploy(params);
      return data;
    },
    onSuccess: () => {
      toast.success('Task run successfully');
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        toastSomethingError(error.response?.data?.details);
      } else {
        toastSomethingError(error.message);
      }
    },
  });

  const handleDeploy = () => {
    mutate({
      files: [
        {
          name: fileName + '.py',
          content: toBase64(code),
        },
      ],
      config: {},
      requirements_content: '',
      project_id: projectId!,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div />
        {/* <div className="flex items-center gap-2">
          <Input
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
          />
          <span>.py</span>
        </div> */}
        <div className="flex items-center gap-2">
          <Button
            variant={'default'}
            startIcon={
              isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RocketIcon className="h-4 w-4" />
              )
            }
            onClick={handleDeploy}
            disabled={isPending}
          >
            Deploy
          </Button>
        </div>
      </div>
      <div className="h-[500px] w-full overflow-hidden">
        <Editor
          height="100%"
          width="100%"
          defaultLanguage="python"
          value={code}
          onMount={handleEditorDidMount}
          onChange={(value) => {
            setCode(value!);
          }}
          options={{
            minimap: {
              enabled: false,
            },
            padding: {
              top: 10,
            },
          }}
          // theme="vs-dark"
          className="border-2"
        />
      </div>

      {/* output */}
      {data && (
        <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-green-700">
              Deployment completed successfully! View your endpoint details and monitor its status. 
            </div>
            <Link
              to={PATH_PAGE.dashboard.serverlessGPU.byId(
                workspaceId!,
                projectId!,
              ).deploy}
            >
              <Button variant={'outline'} className="border-green-200 hover:bg-green-100">
                View Endpoint Details
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServerlessGPUPlaygroundDeploy;
